import { FLEET_MISSION_DEFINITIONS, type FlightLog, type PlayerRace, type SpyReport, type SpyReportTier } from '@kaeliux/shared';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import ShipManifestList from './ShipManifestList';
import ResourceQuantities from './ResourceQuantities';
import { hasResourceQuantities } from '../utils/resourceQuantities';
import { formatCoords, formatDateTime } from '../utils/fleetFormat';

/**
 * Details dialog for the Fleets page's "Flight Logs" grid and "Espionage
 * Reports" section — takes exactly one of `log`/`spyReport`. Battle/Sun
 * Support logs never open this: they navigate to `BattleReportPage`
 * instead (see FleetsPage.tsx).
 */
export interface FlightLogDetailsModalProps {
    open: boolean;
    onClose: () => void;
    race: PlayerRace;
    log?: FlightLog | null;
    spyReport?: SpyReport | null;
}

function LogDetails({ log, race }: { log: FlightLog; race: PlayerRace }) {
    const resources = log.resources && hasResourceQuantities(log.resources) ? log.resources : null;

    return (
        <div className="flex flex-col gap-3">
            <p className="text-content-muted text-sm">
                {formatCoords(log.origin)} → {formatCoords(log.target)}
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs text-content-muted">
                <div>
                    <span className="text-content-muted block text-micro">Departed</span>
                    <span className="text-content-primary">{formatDateTime(log.departedAt)}</span>
                </div>
                <div>
                    <span className="text-content-muted block text-micro">{log.event === 'arrival' ? 'Arrived' : 'Returned'}</span>
                    <span className="text-content-primary">{formatDateTime(log.occurredAt)}</span>
                </div>
            </div>
            <p className="text-content-secondary text-sm italic">{log.message}</p>
            {log.colonyPlanetId && (
                <p className="text-status-success text-sm">A new colony was founded.</p>
            )}
            <div>
                <h3 className="text-content-secondary text-sm font-semibold mb-1">Ships</h3>
                <ShipManifestList ships={log.ships} race={race} />
            </div>
            {resources && (
                <div>
                    <h3 className="text-content-secondary text-sm font-semibold mb-1">Resources</h3>
                    <p className="text-content-primary text-sm"><ResourceQuantities amounts={resources} /></p>
                </div>
            )}
        </div>
    );
}

const SPY_REPORT_TIER_LABELS: Record<SpyReportTier, string> = {
    full: 'Full Intel',
    basic: 'Basic Intel',
    none: 'No Intel',
};

function SpyReportDetails({ report, race }: { report: SpyReport; race: PlayerRace }) {
    return (
        <div className="flex flex-col gap-3">
            <p className="text-content-muted text-sm">
                {formatCoords(report.target)} · Captured {formatDateTime(report.capturedAt)}
            </p>
            <Badge tone="info" data-testid="spy-report-tier">
                {SPY_REPORT_TIER_LABELS[report.tier]}
            </Badge>
            <div>
                <h3 className="text-content-secondary text-sm font-semibold mb-1">Resources</h3>
                <p className="text-content-primary text-sm">
                    <ResourceQuantities amounts={report.resources} includeZero />
                </p>
            </div>
            <div>
                <h3 className="text-content-secondary text-sm font-semibold mb-1">Energy</h3>
                <p className="text-content-primary text-sm">
                    <ResourceQuantities amounts={{ energy: report.energy }} includeZero />
                </p>
            </div>
            {report.ships && (
                <div>
                    <h3 className="text-content-secondary text-sm font-semibold mb-1">Ships</h3>
                    <ShipManifestList ships={report.ships} race={race} />
                </div>
            )}
            {report.tier === 'basic' && (
                <p className="text-content-muted text-xs">
                    A Full report — driven by both players&apos; Intelligence science — would also reveal the target&apos;s docked ships.
                </p>
            )}
        </div>
    );
}

export default function FlightLogDetailsModal({ open, onClose, race, log, spyReport }: FlightLogDetailsModalProps) {
    if (!open || (!log && !spyReport)) return null;

    const title = log
        ? `${FLEET_MISSION_DEFINITIONS[log.mission].name} — ${log.event === 'arrival' ? 'Arrived' : 'Returned'}`
        : `Espionage Report — ${spyReport!.targetPlanetName ?? formatCoords(spyReport!.target)}`;

    return (
        <Modal open={open} onClose={onClose} title={title} size="md">
            {log ? <LogDetails log={log} race={race} /> : <SpyReportDetails report={spyReport!} race={race} />}

            <div className="flex justify-end mt-2">
                <Button type="button" variant="secondary" size="md" onClick={onClose}>
                    Close
                </Button>
            </div>
        </Modal>
    );
}
